import { WidgetProps, asNumber, guessType } from '@rjsf/utils';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import useLinkifiedLabel from './useLinkifiedLabel';

const nums = new Set(['number', 'integer']);

/**
 * This is a silly limitation in the DOM where option change event values are
 * always retrieved as strings.
 */
const processValue = (schema: any, value: any) => {
  // "enum" is a reserved word, so only "type" and "items" can be destructured
  const { type, items } = schema;
  if (value === '') {
    return undefined;
  } else if (type === 'array' && items && nums.has(items.type)) {
    return value.map(asNumber);
  } else if (type === 'boolean') {
    return value === 'true';
  } else if (type === 'number') {
    return asNumber(value);
  }

  // If type is undefined, but an enum is present, try and infer the type from
  // the enum values
  if (schema.enum) {
    if (schema.enum.every((x: any) => guessType(x) === 'number')) {
      return asNumber(value);
    } else if (schema.enum.every((x: any) => guessType(x) === 'boolean')) {
      return value === 'true';
    }
  }

  return value;
};

const SelectWidget = ({ schema, id, options, label, required, disabled, readonly, value, multiple, autofocus, onChange, onBlur, onFocus, rawErrors = [] }: WidgetProps) => {
  const { enumOptions, enumDisabled } = options;

  const emptyValue = multiple ? [] : '';

  const _onChange = ({ target: { value: val } }: React.ChangeEvent<{ name?: string; value: unknown }>) => onChange(processValue(schema, val));
  const _onBlur = ({ target: { value: val } }: React.FocusEvent<HTMLInputElement>) => onBlur(id, processValue(schema, val));
  const _onFocus = ({ target: { value: val } }: React.FocusEvent<HTMLInputElement>) => onFocus(id, processValue(schema, val));

  const displayLabel = useLinkifiedLabel(label);

  return (
    <TextField
      id={id}
      label={displayLabel || schema.title}
      select
      value={typeof value === 'undefined' ? emptyValue : value}
      required={required}
      disabled={disabled || readonly}
      error={rawErrors.length > 0}
      onChange={_onChange}
      onBlur={_onBlur}
      onFocus={_onFocus}
      InputLabelProps={{
        shrink: true,
      }}
      SelectProps={{
        multiple: typeof multiple === 'undefined' ? false : multiple,
      }}
      inputProps={{ 'data-testid': typeof multiple === 'undefined' ? 'select_dropdown' : 'multiselect_dropdown' }}
    >
      {(enumOptions as any).map(({ value: val, label: lbl }: any, i: number) => {
        const _disabled: any = enumDisabled && (enumDisabled as any).indexOf(val) != -1;
        return (
          <MenuItem key={i} value={val} disabled={_disabled}>
            {lbl}
          </MenuItem>
        );
      })}
    </TextField>
  );
};

export default SelectWidget;
